/**
 * GENERATED by /docs-writer from docs/tasks.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/tasks.md`.
 *
 * source-sha: 54e0ce40424e535ff083e76730cd86803d133d06
 * sources (from the markdown's "Sources" section):
 */
import { Link } from 'react-router';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'who-can-see-a-task', label: 'Who can see a task' },
  { id: 'create-a-task', label: 'Create a task' },
  { id: 'what-you-can-change-and-who-can', label: 'What you can change, and who can' },
  { id: 'the-access-grant', label: 'The access grant' },
  { id: 'labels', label: 'Labels' },
  { id: 'find-a-task', label: 'Find a task' },
  { id: 'the-board-and-the-weekly-plan', label: 'The board and the weekly plan' },
  { id: 'history', label: 'History' },
  { id: 'notifications', label: 'Notifications' },
];

export function TasksDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Tasks · Guide</p>
        <h1 className="doc-title">Tasks</h1>
        <p className="doc-lede">
          A <strong>task</strong> is the one ticket for anything you need from the DevOps team: access to a
          system, a change to the infrastructure, or anything else. A request for access is a task with an{' '}
          <strong>access grant</strong> on it. A task works like a GitLab issue: a title, a description in
          markdown, the person who created it (the requester), the people <strong>Assigned</strong> to it,
          labels, one due date, and a timeline of activity and comments. There is no priority, checklist or
          links section; talk in the comments.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-29">29 Oct 2026</time>
        </p>

        <p className="doc-p">
          Old links still work: <code>/requests…</code>, <code>/access…</code> and <code>/changes…</code> all
          redirect to <code>/tasks…</code>. <code>/access/&lt;id&gt;</code> and{' '}
          <code>/requests/&lt;id&gt;</code> open the same task, because the numbers did not change. An old
          change request has a new number, and <code>/changes/&lt;old id&gt;</code> takes you to it.
        </p>

        <h2 className="doc-h2" id="who-can-see-a-task">Who can see a task</h2>
        <ul className="doc-list">
          <li>
            A task with an <strong>access grant</strong> is open to everyone who is signed in, a new member
            included. Anyone who can see it can comment on it.
          </li>
          <li>
            Every other task is visible to <strong>engineers and above</strong>, and to the person who filed it. A
            new member sees the tasks they filed; anyone else&rsquo;s task does not exist for them (not in the list,
            not in a search, and its page says "not found").
          </li>
          <li>
            A secret linked to a task (an old task, or one made through the API) shows its name only when your
            role may see that secret&rsquo;s level. Otherwise the link reads "hidden secret". The same applies
            to the link entries on the timeline.
          </li>
        </ul>

        <h2 className="doc-h2" id="create-a-task">Create a task</h2>
        <p className="doc-p">
          Open <Link to="/tasks">Tasks</Link> and press <strong>+ New task</strong>. Pick one of two
          templates. The template only decides which fields the form shows; every task is the same kind of
          ticket afterwards.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Template</th>
                <th>Use it for</th>
                <th>Who can create</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Task</strong></td>
                <td>Anything to do: a title, a description, people, labels and a due date</td>
                <td>Everyone signed in, a new member included</td>
              </tr>
              <tr>
                <td><strong>Access</strong></td>
                <td>Access to a company system</td>
                <td>Everyone signed in, a new member included</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The <strong>Access</strong> template adds the access grant to the task. It asks for the system, the
          target and why. Without a title, the task is titled "Access: <em>system</em> "<em>target</em>"". A
          new member can name the target but cannot pick an inventory entry and cannot add labels. On the{' '}
          <strong>Task</strong> template a new member picks engineers to ask for help, and cannot add labels. See{' '}
          <Link to="/docs/request-access">Requesting access</Link> for the form itself. Old links with{' '}
          <code>?template=change</code> or <code>?template=blank</code> open the Task form.
        </p>
        <p className="doc-p">
          The description, the access "Why" and comments use one markdown editor: headings, bold, italic,
          strikethrough, inline code, lists, task lists, quotes, code blocks, links, tables and images (all
          kept as markdown), and <code>@</code> mentions. <strong>Cmd/Ctrl+Enter</strong> submits and{' '}
          <strong>Esc</strong> cancels an edit.
        </p>
        <h3 className="doc-h3">Writing the description</h3>
        <p className="doc-p">
          This outline is recommended for the description of a plain task. Nothing checks it, so use the
          sections that help and drop the rest. A small chore can be two lines.
        </p>
        <pre className="doc-code">
          <code>{`## Context
Why this task exists: the trigger, related tasks (#123), the goal,
facts already settled.

## Problem
What is wrong or missing, with evidence (file and line, logs, counts).
How often, and who is hit.

## Proposal
The change you intend, step by step.
**Out of scope:** what is left for another task.

## Decision
Dated decisions and who made them ("2026-10-27, Binh: go ahead"),
and the options rejected, with why.

## References
Code paths, merge requests (full links), related tasks, docs.

## Done when
- [ ] An outcome someone can check. When you tick it, add the evidence
      (test, environment, time, MR).`}</code>
        </pre>
        <ul className="doc-list">
          <li>
            <strong>Decision</strong> can start empty and fill in as the task moves. Add to it rather than
            editing older entries.
          </li>
          <li>
            <strong>Done when</strong> is the task&rsquo;s checklist. Steps of one topic stay in one task, as items
            here, not as separate tasks.
          </li>
          <li>
            Agents that file tasks through the MCP <code>create_task</code> tool are asked to use the same outline.
          </li>
        </ul>
        <h3 className="doc-h3">Assigned</h3>
        <p className="doc-p">
          A task can have <strong>several assignees</strong>. On a plain task every assignee must be an engineer
          or above. On an <strong>access task</strong> the <strong>default access assignees</strong> are always
          assigned when it is created (root and maintainers keep that list on Settings → Default access
          assignees; today it is two people). The requester may add anyone with a staff account on top of them,
          whatever their role, and may remove a default on the form before creating. The requester cannot be an
          assignee of their own access task.
        </p>
        <h3 className="doc-h3">The due date</h3>
        <p className="doc-p">
          A task has <strong>one date</strong>, its due date (<strong>Access until</strong> on an access
          task). It is the task&rsquo;s deadline. On an access task it is also when the access ends: the grant
          is valid until the due date, and an empty due date means the access is permanent. Changing the due
          date of an access task changes when the access ends. The app does not remove access when the date
          passes. A due date can be at most one day in the past.
        </p>

        <h2 className="doc-h2" id="what-you-can-change-and-who-can">What you can change, and who can</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th>Who</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Edit the title, description or due date</td>
                <td>The requester, any assignee, or root</td>
              </tr>
              <tr>
                <td>Edit the <strong>reason</strong> of a task with an access grant (its description)</td>
                <td>The requester only</td>
              </tr>
              <tr>
                <td>Add or remove an assignee</td>
                <td>
                  The requester, any assignee, or root. On a plain task a new assignee must be an engineer or above;
                  on an access task it can be anyone but the requester. A new member can do this only on their own
                  task
                </td>
              </tr>
              <tr>
                <td>Close as <strong>done</strong> or <strong>not planned</strong>, reopen</td>
                <td>
                  The requester, any assignee, or root. A new member only on their own plain task; they withdraw an
                  access task with <strong>Cancel request</strong>
                </td>
              </tr>
              <tr>
                <td>Cancel</td>
                <td>The requester, or root</td>
              </tr>
              <tr>
                <td>Comment</td>
                <td>Anyone who can see the task</td>
              </tr>
              <tr>
                <td>Edit a comment</td>
                <td>Its author</td>
              </tr>
              <tr>
                <td>Delete a comment</td>
                <td>Its author, or root</td>
              </tr>
              <tr>
                <td>Add or remove labels, move cards on the board</td>
                <td>
                  Every engineer and above, on an open task. <code>workflow::</code> labels change only
                  through the board or the task&rsquo;s <strong>Board column</strong> control
                </td>
              </tr>
              <tr>
                <td>Create, rename, recolor, archive or delete labels</td>
                <td>Root and maintainers</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          A <strong>closed</strong> task accepts nothing but comments and <strong>Reopen</strong>. The last
          assignee can be removed; the task is then unassigned.
        </p>

        <h2 className="doc-h2" id="the-access-grant">The access grant</h2>
        <p className="doc-p">
          The access grant has its own status: <strong>pending</strong>, <strong>approved</strong>,{' '}
          <strong>denied</strong> or <strong>cancelled</strong>.
        </p>
        <ul className="doc-list">
          <li>
            <strong>Who decides:</strong> any assignee of that task (whatever their role, a new member included), a
            maintainer, or root. They approve or deny with a note; the note is required.
          </li>
          <li>
            Nobody decides their own task except root. A root self-decision is marked as such on the timeline
            and in Slack.
          </li>
          <li>
            There is <strong>no layered review</strong>: no layers, no escalation, no maintainer sign-off and
            no risk tier. If someone else should decide, add them as an assignee.
          </li>
          <li>The requester can cancel while it is pending. Root can cancel anyone&rsquo;s.</li>
          <li>Root and maintainers can correct the decision note afterwards.</li>
        </ul>
        <p className="doc-p">
          The decision also closes the task: <strong>approved</strong> closes it as done (and moves it to{' '}
          <code>workflow::done</code> if it was on the board), <strong>denied</strong> as not planned (and
          takes it off the board), <strong>cancelled</strong> as cancelled. A task whose access was decided
          cannot be reopened; create a new one. While the grant is pending, the task cannot be closed as done
          or not planned: <strong>Cancel</strong> it instead, which closes it and marks the grant cancelled.
        </p>

        <h2 className="doc-h2" id="labels">Labels</h2>
        <p className="doc-p">
          Labels are free text, such as <code>blocked</code> or <code>team::db</code>. A label with{' '}
          <code>::</code> is <strong>scoped</strong>: the part before the last <code>::</code> is its scope,
          and a task carries at most one label per scope. Adding <code>team::net</code> to a task that
          has <code>team::db</code> swaps them, and the timeline records why.
        </p>
        <p className="doc-p">Some labels come with the app:</p>
        <ul className="doc-list">
          <li>
            <code>workflow::todo</code>, <code>workflow::in-progress</code>, <code>workflow::done</code>: the
            board columns. These are system labels and cannot be renamed, archived or deleted, and nobody can
            create another <code>workflow::</code> label. They are not offered in the label pickers: a task
            changes column on the <Link to="/tasks/board">board</Link> or with the{' '}
            <strong>Board column</strong> control in the task&rsquo;s sidebar (engineers and above), which
            makes the same move as a drag.
          </li>
          <li>
            <code>type::secret-rotation</code>, <code>type::deploy-config</code>,{' '}
            <code>type::db-change</code>: the kinds of the old change requests.
          </li>
          <li><code>blocked</code>.</li>
        </ul>
        <p className="doc-p">
          Root and maintainers manage labels on <Link to="/settings/labels">Labels</Link>. A label that is in
          use cannot be renamed into a different scope. An archived label stays on the tasks that have it but
          cannot be added again. A label can be deleted once it is not on any task; remove it from its tasks
          first, or archive it. The timeline keeps the deleted label&rsquo;s name on its old entries.
        </p>

        <h2 className="doc-h2" id="find-a-task">Find a task</h2>
        <p className="doc-p">
          The <Link to="/tasks">Tasks</Link> list has one filter box. Type words to search the title, the
          description, the people (any assignee included), the access target and label names (<code>#12</code>{' '}
          and <code>CR-7</code> work too), and add filters from <strong>Add filter</strong>. The whole filter
          is one <code>q</code> in the address, so a filtered list is a link you can share.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Filter</th>
                <th>Values</th>
                <th>Shows</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>state:</code></td>
                <td><code>open</code>, <code>closed</code></td>
                <td>Open or closed tasks</td>
              </tr>
              <tr>
                <td><code>label:</code></td>
                <td>any label name; quote one with a space: <code>label:"needs review"</code></td>
                <td>Tasks with that label. Repeat it to require several</td>
              </tr>
              <tr>
                <td><code>access:</code></td>
                <td>
                  <code>any</code>, <code>pending</code>, <code>approved</code>, <code>denied</code>,{' '}
                  <code>cancelled</code>
                </td>
                <td>Tasks with an access grant, or with a grant in that status</td>
              </tr>
              <tr>
                <td><code>assignee:</code> / <code>requester:</code></td>
                <td><code>me</code> or an email</td>
                <td>Tasks where that person is one of the assignees, or that they created</td>
              </tr>
              <tr>
                <td><code>workflow:</code></td>
                <td><code>todo</code>, <code>in-progress</code>, <code>done</code>, <code>none</code></td>
                <td>That board column; <code>none</code> = open tasks with no <code>workflow::</code> label</td>
              </tr>
              <tr>
                <td><code>awaiting:</code></td>
                <td><code>me</code></td>
                <td><strong>Waiting on me</strong>, below</td>
              </tr>
              <tr>
                <td><code>due:</code></td>
                <td><code>overdue</code>, <code>soon</code></td>
                <td>Past due, or due within 7 days. Implies <code>state:open</code> unless you set a state</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          Every filter except <code>label:</code> takes one value; a second <code>state:</code> replaces the
          first. An unknown value is dropped rather than sent.
        </p>
        <p className="doc-p">
          <strong>Waiting on me</strong> (<code>awaiting:me</code>) lists the open tasks whose access grant is
          pending and that you can decide: you are one of the assignees, or you are root or a
          maintainer. Your own tasks are never in it.
        </p>

        <h2 className="doc-h2" id="the-board-and-the-weekly-plan">The board and the weekly plan</h2>
        <p className="doc-p">
          <Link to="/tasks/board">Board</Link> shows the tasks that carry a workflow label, for every engineer
          and above. A new member who opens it sees a note that the board needs the engineer role; the board
          is not in their menu.
        </p>
        <ul className="doc-list">
          <li>
            <strong>No workflow label</strong> is the source column: open tasks with no workflow label, in the
            order the team set, then newest-updated first. It starts collapsed (the board remembers your
            choice), loads 50 cards at a time with <strong>Load more</strong>, and follows the board&rsquo;s
            assignee, label and search filters. The assignee filter matches any assignee. Dragging a card out
            of it plans the task; dragging one into it takes it off the plan.
          </li>
          <li><strong>TODO</strong> and <strong>IN PROGRESS</strong> show every open task with that label.</li>
          <li>
            <strong>DONE</strong> shows the tasks moved to done <strong>this week</strong>. Pick another week,
            or <strong>All</strong> for the last 30 days (at most 100 cards).
          </li>
          <li>
            Cards are in the order the team set by dragging. Cards nobody has placed come after them, sorted
            automatically: by due date (no date last), then last update; in <strong>No workflow label</strong>,
            newest update first; in DONE <strong>All</strong>, the most recently done first.
          </li>
          <li>
            A card shows each label as a short bar in the label&rsquo;s colour; hover a bar for its name. Turn on
            <strong>Label names</strong> above the board to show solid chips with the names instead; a scoped
            label then shows its scope in the solid colour and its value beside it. The board remembers your
            choice in this browser. The <code>workflow::</code> label is not shown: the column says it.
          </li>
        </ul>
        <p className="doc-p">
          <strong>Changing the order.</strong> Drag a card up or down inside its column, in any column, DONE
          included (with the keyboard: Space, then Up or Down, then Space). The order is shared: everyone sees
          the same order. Reordering is not an update: it adds nothing to the task&rsquo;s history and does not
          change its <strong>Updated</strong> time, so lists sorted by last update are not affected. Dropping a
          card into another column puts it where you dropped it. A card that changes column without a position
          goes to the <strong>bottom</strong> of its new column: a new task, a column changed from the
          task&rsquo;s page, a task closed as done, a reopened task. With a filter on, the card goes right after
          the visible card above it; cards the filter hides keep their place.
        </p>
        <p className="doc-p">
          Weeks start on <strong>Monday 00:00 Vietnam time</strong> and are numbered like{' '}
          <code>2026-W42</code>. A past week is read-only: cards cannot be dragged until you switch back to
          this week.
        </p>
        <p className="doc-p">
          Putting a task in TODO plans it for the current week. Taking its workflow label off removes it from
          the plan. Moving a card to <strong>DONE</strong> closes the task as done; moving a closed card out
          of DONE reopens it. Closing a task from its own page as done puts it in DONE; closing it as not
          planned or cancelling it takes it off the board.
        </p>
        <p className="doc-p">
          An access task can be on the board too. It cannot be moved to DONE while the grant is pending. When
          the grant is approved, the card moves to DONE by itself. When it is denied, the card leaves the
          board and counts as dropped, not done.
        </p>
        <p className="doc-p">
          The board does nothing when a new week starts. A card still in TODO or IN PROGRESS from an earlier
          week shows <strong>since W41</strong>, and counts as carried over.
        </p>
        <p className="doc-p">
          Every move tells the server which column the card came from. If someone else moved the card while
          you were dragging it, the server refuses the move with a <code>CONFLICT</code> error (HTTP 409), the
          card goes back, the board says the card was moved by someone else, and it reloads. The same happens
          when the card you dropped next to has left the column, or someone reordered those cards first. Any
          other refusal (for example DONE while a grant is pending) shows the server&rsquo;s reason and
          reloads too.
        </p>

        <h2 className="doc-h2" id="history">History</h2>
        <p className="doc-p">
          Every change writes one entry to the task&rsquo;s timeline: who did it, when, and in which week.
          Adding or removing an assignee writes <code>assignee_added</code> / <code>assignee_removed</code>.
          Label changes record the label, the assignees at that moment, and where it happened (board, task
          page, closing, an access decision, or the migration). A board move writes the removal and the
          addition as one move. Entries from before tasks (<code>assigned</code>, <code>escalated</code>,
          maintainer sign-off) stay on the timeline as history.
        </p>
        <p className="doc-p">
          From this history the app keeps three ready-made views for the weekly and monthly reports:
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>View</th>
                <th>One row per</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>v_task_label_history</code></td>
                <td>Label added or removed</td>
              </tr>
              <tr>
                <td><code>v_task_workflow_spans</code></td>
                <td>
                  Stretch a task spent in one workflow column, with when it entered and left; cycle time comes
                  from here
                </td>
              </tr>
              <tr>
                <td><code>v_task_weekly_outcome</code></td>
                <td>
                  Week and task, with five flags: <strong>planned new</strong>, <strong>carried in</strong>,{' '}
                  <strong>done</strong>, <strong>dropped</strong>, <strong>carried over</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          A task is <strong>carried in</strong> when it held TODO or IN PROGRESS as the week began, and{' '}
          <strong>carried over</strong> when it still held one as the week ended. It is{' '}
          <strong>dropped</strong> when it was in the week&rsquo;s plan, was not finished, and lost its
          workflow label before the week ended.
        </p>

        <h2 className="doc-h2" id="notifications">Notifications</h2>
        <ul className="doc-list">
          <li>
            A new task posts to the tasks Slack channel. A new access task also emails its assignees (not you)
            and sends a browser push to everyone who opted in, except the requester.
          </li>
          <li>
            Being added as an assignee emails you ("Assigned to you"), unless the access email already reached
            you. On an access task you are told whatever your role, because you can decide it; on a plain task
            only engineers and above are. You are never told about your own action.
          </li>
          <li>
            A decision emails the requester, sends them a push, and posts to Slack (approved and denied only).
            A cancellation emails and pushes the requester but does not post to Slack.
          </li>
          <li>Closing a task without an access grant emails the requester.</li>
          <li>
            An <code>@mention</code> emails the person mentioned. On an access task it also sends them a push.
            On a task without an access grant, only engineers and above are emailed.
          </li>
          <li>
            A task that is due within 24 hours, or overdue, emails every assignee once per due date (the
            requester when there is no assignee). Changing the due date re-arms the reminder.
          </li>
        </ul>
        <p className="doc-p">
          Browser push is off until you turn it on in the Notification center on your{' '}
          <Link to="/settings/profile">Account</Link> page. Every link in these messages opens{' '}
          <code>/tasks/&lt;id&gt;</code>. Email and Slack are sent only where they are set up, and a failed
          message never undoes the change.
        </p>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
